import React, { useEffect, useRef, useState } from 'react';
import { DropdownFilter, type DropdownFilterOption } from '@ui';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-solid-svg-icons';
import Input from '@components/ui/Input';
import { useOutsideClick } from '@hooks/useOutsideClick';
import LogsDateRangeFilter, {
  LOGS_DEFAULT_DATE_RANGE,
  type LogsDateRangeValue,
} from './LogsDateRangeFilter';
import LogsCalendarPicker from './LogsCalendarPicker';
import { toBackendDate, toInputDate, formatDisplayDate } from './dateUtils';

// ── Types ─────────────────────────────────────────────────────────────────────

export interface LogsFilters {
  search: string;
  dateRange: LogsDateRangeValue;
  /** `null` = "ALL". */
  status: string | null;
  /** `null` = "ALL". AceUp staff only — always `null` for Partner roles. */
  organizationId: string | null;
  /** `null` = "ALL". */
  companyId: string | null;
  /** `null` = "ALL". */
  /** `null` = "ALL". HTTP method (GET / POST / …) — shown as "Event type". */
  httpMethod: string | null;
}

/** Every filter at its sc-44216 "page load" default. */
export const DEFAULT_LOGS_FILTERS: LogsFilters = {
  search: '',
  dateRange: LOGS_DEFAULT_DATE_RANGE,
  status: null,
  organizationId: null,
  companyId: null,
  httpMethod: null,
};

interface LogsFilterBarProps {
  /** The currently-applied filter selections — every control auto-applies as soon as it changes. */
  filters: LogsFilters;
  /** Commits a new filter set immediately — the events table refetches right away. */
  onChange: (filters: LogsFilters) => void;
  /** Resets every filter back to default, immediately. */
  onClear: () => void;
  isAceUpStaff: boolean;
  organizationOptions: DropdownFilterOption[];
  companyOptions: DropdownFilterOption[];
  /** HTTP methods present in the caller's logs, for the "Event type" dropdown. */
  httpMethodOptions: DropdownFilterOption[];
}

// ── Helpers ───────────────────────────────────────────────────────────────────

/** Sentinel value for the "ALL" option — DropdownFilter options need a string value, so `null` alone can't be one of them. */
const ALL_VALUE = '__all__';

const withAllOption = (options: DropdownFilterOption[]): DropdownFilterOption[] => [
  { label: 'All', value: ALL_VALUE },
  ...options,
];

const STATUS_OPTIONS: DropdownFilterOption[] = [
  { label: 'Success', value: 'success' },
  { label: 'Error', value: 'error' },
];

const toSingleValue = (value: string[] | string | null): string | null => {
  const raw = Array.isArray(value) ? (value[0] ?? null) : value;
  return raw && raw !== ALL_VALUE ? raw : null;
};

/** The trigger text when nothing (or an unknown value) is selected — the field's own label sits above it. */
const labelFor = (options: DropdownFilterOption[], value: string | null): string => {
  if (!value) return 'All';
  const match = options.find((o) => o.value === value);
  return typeof match?.label === 'string' ? match.label : value;
};

// ── Component ─────────────────────────────────────────────────────────────────

/**
 * Filter bar for the Logs page (sc-44216): search + date range + status +
 * (AceUp-only) org + company + event type.
 *
 * Every control auto-applies the moment it changes (`onChange` is what the
 * events table is actually built from — there's no separate "draft" state
 * to commit). "Clear filters" (`onClear`) resets everything back to default,
 * also immediately.
 *
 * The one exception is the custom date range: picking "Custom range" and
 * typing a From/To is staged in *local* state below and only reaches
 * `onChange` once the row's own small Apply is clicked — a lone `from` with
 * no `to` yet (or vice versa) isn't a fetchable range, so there's nothing
 * sensible to auto-apply until both are picked.
 */
const LogsFilterBar: React.FC<LogsFilterBarProps> = ({
  filters,
  onChange,
  onClear,
  isAceUpStaff,
  organizationOptions,
  companyOptions,
  httpMethodOptions,
}) => {
  const hasActiveFilters =
    Boolean(filters.search) ||
    filters.dateRange.preset !== DEFAULT_LOGS_FILTERS.dateRange.preset ||
    Boolean(filters.status) ||
    Boolean(filters.organizationId) ||
    Boolean(filters.companyId) ||
    Boolean(filters.httpMethod);

  const isCustomApplied = filters.dateRange.preset === 'custom';

  // Local, unapplied staging for the custom-range row — deliberately not
  // derived from `filters` alone, since the row needs to appear (and hold
  // in-progress From/To picks) before anything has actually been applied.
  const [showCustomRow, setShowCustomRow] = useState(isCustomApplied);
  const [customFrom, setCustomFrom] = useState(
    isCustomApplied && filters.dateRange.from ? toInputDate(filters.dateRange.from) : ''
  );
  const [customTo, setCustomTo] = useState(
    isCustomApplied && filters.dateRange.to ? toInputDate(filters.dateRange.to) : ''
  );

  // Which custom-range calendar popup (Figma node 2771-9999) is open, if
  // any — at most one of From/To can be open at a time.
  const [openCalendar, setOpenCalendar] = useState<'from' | 'to' | null>(null);
  const fromFieldRef = useRef<HTMLDivElement>(null);
  const toFieldRef = useRef<HTMLDivElement>(null);

  // Close whichever calendar popup is open on an outside click.
  useOutsideClick(fromFieldRef, () => setOpenCalendar(null), openCalendar === 'from');
  useOutsideClick(toFieldRef, () => setOpenCalendar(null), openCalendar === 'to');

  // Re-sync whenever the *applied* date range changes from outside this
  // staging (a preset click, Clear filters, or this row's own Apply) —
  // otherwise the row could keep showing stale picks after any of those.
  useEffect(() => {
    setShowCustomRow(isCustomApplied);
    setCustomFrom(
      isCustomApplied && filters.dateRange.from ? toInputDate(filters.dateRange.from) : ''
    );
    setCustomTo(isCustomApplied && filters.dateRange.to ? toInputDate(filters.dateRange.to) : '');
    setOpenCalendar(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.dateRange.preset, filters.dateRange.from, filters.dateRange.to]);

  const canApplyCustomRange = Boolean(customFrom && customTo);

  // sc-45715: once the user steps into custom-range editing (picks "Custom
  // range" from the dropdown), the trigger should read "Custom" right away
  // instead of continuing to show whatever preset/range is still actually
  // applied — leaving it on e.g. "Last 7 days" while the From/To/Apply row
  // is visibly open for a *different* range read as a contradiction.
  const dateRangeControl = (
    <LogsDateRangeFilter
      value={showCustomRow ? { preset: 'custom' } : filters.dateRange}
      onChange={(dateRange) => onChange({ ...filters, dateRange })}
      onSelectCustom={() => setShowCustomRow(true)}
    />
  );

  const handleApplyCustomRange = () => {
    if (!canApplyCustomRange) return;
    onChange({
      ...filters,
      dateRange: { preset: 'custom', from: toBackendDate(customFrom), to: toBackendDate(customTo) },
    });
  };

  // Clearing needs to force the row closed directly rather than relying on
  // the re-sync effect above: if "Custom range" was picked but never
  // actually applied (no From/To committed yet), `filters.dateRange` is
  // still whatever preset it always was — clearing doesn't change that
  // value at all, so the effect's dependencies never change and it never
  // re-runs, leaving the row stuck open with its unapplied picks.
  const handleClear = () => {
    setShowCustomRow(false);
    setCustomFrom('');
    setCustomTo('');
    setOpenCalendar(null);
    onClear();
  };

  // Layout follows the Design System prototype (logs.html `.filters`): one
  // row of labelled fields — Search, Event status, Org (AceUp staff only),
  // Company, Event type, Date range — with the custom From → To + Apply row
  // opening underneath while "Custom range" is picked.
  return (
    <div className="lf-filter-bar" role="search">
      <div className={`lf-filter-grid${isAceUpStaff ? '' : ' lf-filter-grid--partner'}`}>
        <div className="lf-field lf-field--search">
          <span className="lf-field-label">Search</span>
          <Input
            type="text"
            placeholder="Search by..."
            aria-label="Search by..."
            value={filters.search}
            onChange={(e) => onChange({ ...filters, search: e.target.value })}
          />
        </div>

        <div className="lf-field">
          <span className="lf-field-label">Event status</span>
          <DropdownFilter
            name="logs-status-filter"
            filterLabel={labelFor(STATUS_OPTIONS, filters.status)}
            options={withAllOption(STATUS_OPTIONS)}
            value={filters.status ?? ALL_VALUE}
            onChange={(_name, value) => onChange({ ...filters, status: toSingleValue(value) })}
          />
        </div>

        {isAceUpStaff && (
          <div className="lf-field">
            <span className="lf-field-label">Org</span>
            <DropdownFilter
              name="logs-org-filter"
              filterLabel={labelFor(organizationOptions, filters.organizationId)}
              options={withAllOption(organizationOptions)}
              value={filters.organizationId ?? ALL_VALUE}
              onChange={(_name, value) =>
                // Switching orgs invalidates the previously-selected company —
                // same reasoning as UserAnalytics's org/company coupling.
                onChange({ ...filters, organizationId: toSingleValue(value), companyId: null })
              }
            />
          </div>
        )}

        {/* Partner roles never see the Org filter, so Company always stays
            clickable for them (it's implicitly scoped to their own org).
            For AceUp staff, Company is disabled (not hidden — the layout
            stays stable) until they've narrowed to one specific org,
            since "All orgs" has no single company list to pick from. */}
        <div className="lf-field">
          <span className="lf-field-label">Company</span>
          <DropdownFilter
            name="logs-company-filter"
            filterLabel={labelFor(companyOptions, filters.companyId)}
            options={withAllOption(companyOptions)}
            value={filters.companyId ?? ALL_VALUE}
            disabled={isAceUpStaff && !filters.organizationId}
            onChange={(_name, value) => onChange({ ...filters, companyId: toSingleValue(value) })}
          />
        </div>

        <div className="lf-field">
          <span className="lf-field-label">Event type</span>
          <DropdownFilter
            name="logs-event-type-filter"
            filterLabel={labelFor(httpMethodOptions, filters.httpMethod)}
            options={withAllOption(httpMethodOptions)}
            value={filters.httpMethod ?? ALL_VALUE}
            onChange={(_name, value) => onChange({ ...filters, httpMethod: toSingleValue(value) })}
          />
        </div>

        <div className="lf-field">
          <span className="lf-field-label">Date range</span>
          {dateRangeControl}
        </div>
      </div>

      {/* Custom range (prototype `.au-date-custom`): From → To + Apply, shown
          as soon as "Custom range" is picked. Nothing here reaches the query
          until that Apply is clicked (see the component doc comment). */}
      {showCustomRow && (
        <div className="lf-custom-range-row">
          <div className="lf-field lf-field--date" ref={fromFieldRef}>
            <span className="lf-field-label">From</span>
            <button
              type="button"
              className="lf-custom-range-field"
              aria-label="From date"
              aria-haspopup="dialog"
              aria-expanded={openCalendar === 'from'}
              onClick={() => setOpenCalendar((c) => (c === 'from' ? null : 'from'))}
            >
              <span className="lf-custom-range-field-icon" aria-hidden>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect
                    x="1.5"
                    y="2.5"
                    width="11"
                    height="10"
                    rx="1.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                  />
                  <path d="M1.5 5.5H12.5" stroke="currentColor" strokeWidth="1.2" />
                  <path
                    d="M4 1.5V3.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M10 1.5V3.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span
                className={`lf-custom-range-field-text${customFrom ? '' : ' lf-custom-range-field-text--placeholder'}`}
              >
                {customFrom ? formatDisplayDate(customFrom) : 'mm/dd/yyyy'}
              </span>
            </button>

            {/* Custom calendar popup — replaces the browser's native date
                picker so the control matches the app across browsers/OSes. */}
            {openCalendar === 'from' && (
              <LogsCalendarPicker
                ariaLabel="From date"
                value={customFrom || undefined}
                max={customTo || undefined}
                onSelect={(iso) => {
                  setCustomFrom(iso);
                  setOpenCalendar(null);
                }}
                onClear={() => {
                  setCustomFrom('');
                  setOpenCalendar(null);
                }}
              />
            )}
          </div>

          <span className="lf-custom-range-arrow" aria-hidden>
            →
          </span>

          <div className="lf-field lf-field--date" ref={toFieldRef}>
            <span className="lf-field-label">To</span>
            <button
              type="button"
              className="lf-custom-range-field"
              aria-label="To date"
              aria-haspopup="dialog"
              aria-expanded={openCalendar === 'to'}
              onClick={() => setOpenCalendar((c) => (c === 'to' ? null : 'to'))}
            >
              <span className="lf-custom-range-field-icon" aria-hidden>
                <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                  <rect
                    x="1.5"
                    y="2.5"
                    width="11"
                    height="10"
                    rx="1.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                  />
                  <path d="M1.5 5.5H12.5" stroke="currentColor" strokeWidth="1.2" />
                  <path
                    d="M4 1.5V3.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                  <path
                    d="M10 1.5V3.5"
                    stroke="currentColor"
                    strokeWidth="1.2"
                    strokeLinecap="round"
                  />
                </svg>
              </span>
              <span
                className={`lf-custom-range-field-text${customTo ? '' : ' lf-custom-range-field-text--placeholder'}`}
              >
                {customTo ? formatDisplayDate(customTo) : 'mm/dd/yyyy'}
              </span>
            </button>

            {openCalendar === 'to' && (
              <LogsCalendarPicker
                ariaLabel="To date"
                value={customTo || undefined}
                min={customFrom || undefined}
                onSelect={(iso) => {
                  setCustomTo(iso);
                  setOpenCalendar(null);
                }}
                onClear={() => {
                  setCustomTo('');
                  setOpenCalendar(null);
                }}
              />
            )}
          </div>

          {/* The only "Apply" in the bar — commits the staged From/To (and
              only the date range; every other filter applies itself the
              moment it changes). */}
          <button
            type="button"
            className="lf-custom-range-apply"
            disabled={!canApplyCustomRange}
            onClick={handleApplyCustomRange}
          >
            Apply
          </button>
        </div>
      )}

      {/* "Clear filters" — only once something differs from the defaults. */}
      {hasActiveFilters && (
        <div className="lf-filter-actions">
          <button type="button" className="lf-clear-link" onClick={handleClear}>
            <FontAwesomeIcon icon={faXmark} />
            Clear filters
          </button>
        </div>
      )}
    </div>
  );
};

export default LogsFilterBar;
